import TrueVue from 'vue'; // We name it this so that it won't clash with integrations and we can only import that piece
import * as Sentry from '@sentry/browser';
import { Vue } from '@sentry/integrations';
import html2canvas from 'html2canvas';

Sentry.init({
	enabled: process.env.VUE_APP_ENV !== 'local',
	dsn: process.env.VUE_APP_SENTRY_KEY,
	environment: process.env.VUE_APP_ENV,
	integrations: [new Vue({ Vue: TrueVue, attachProps: true })],
	normalizeDepth: 100,
	maxBreadcrumbs: 5,
	maxValueLength: 1000
});

// Some constants that will make on-demand processing easier
const objProto = Object.prototype.toString.call({});
const ignoredKeys = ['name', 'level', 'timestamp'];
const screenshotError = 'Error uploading screenshot';

// We must define our logger options before anyone imports it!
window.logOpts = {
	name: 'wholesale-ui-avana',
	LOG_LEVEL: 'info',
	DEPLOY_ENV: process.env.VUE_APP_ENV
};

// Now to hijack the error console so we can send all of our error logs to Sentry!
const originalErrorConsole = console.error;
console.error = function(...args) {
	Sentry.configureScope(async scope => {
		// Configure some contextual info to help us in debugging
		const currentUser = TrueVue.prototype.$store.User.currentUser;
		scope.setUser({ ...currentUser });
		scope.setLevel('error');

		if (args[0] && args[0].message !== screenshotError) {
			try {
				// Grab a screenshot
				const canvas = await html2canvas(document.body, { allowTaint: true });
				const screenshotData = canvas.toDataURL('image/jpeg', 0.3).replace(/^data:image\/jpeg;base64,/, '');

				// Now upload it to our image storage server and get a URL to upload to Sentry
				const response = await window.fetch(process.env.VUE_APP_SENTRY_IMAGE_URL, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json'
					},
					body: JSON.stringify({ app: window.logOpts.name, env: window.logOpts.DEPLOY_ENV, imageData: screenshotData })
				});

				// Decode and give the URL to Sentry
				const json = response.json();
				scope.setExtra('screenshot', json.imageUrl);
			} catch (e) {
				setImmediate(() => {
					// Wait a moment and then log *this* as an error, lol
					console.error({ error: e, message: screenshotError });
				});
			}
		}

		// Cycle through all args and gather their info
		const fingerprints = [];
		for (const arg of args) {
			if (Object.prototype.toString.call(arg) === objProto) {
				// We just want to make sure that we don't upload any of the redundant Bunyan props
				Object.keys(arg)
					.filter(key => !ignoredKeys.includes(key))
					.forEach(key => {
						scope.setExtra(key, arg[key]);
						fingerprints.push(arg[key]);
					});
			} else {
				scope.setExtra(arg, arg);
				fingerprints.push(arg);
			}
		}

		// Fingerprints are a way to group our exceptions
		scope.setFingerprint(['{{ default }}', ...fingerprints, currentUser.id, currentUser.companyId, currentUser.role]);

		// Grab our message
		const message = args[0].message || args[0];

		// Format and submit our error
		const newError = new Error(message);

		// Remove logHandler from the stack trace - so that we don't see that in Sentry
		const stackTrace = newError.stack;
		if (stackTrace) {
			const splitStack = stackTrace.split('\n'); // Create an array with all lines
			splitStack.splice(1, 1); // Remove the second line (first line after "ERROR")
			newError.stack = splitStack.join('\n');
		}

		Sentry.captureException(newError);
	});

	if (originalErrorConsole) {
		originalErrorConsole(...args);
	}
};
